"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Bot, Send, Sparkles, X } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";

type Msg = { role: "user" | "assistant"; content: string };

/* Saudi-dialect suggestion pool — a random set is shown each time */
const SUGGESTIONS_AR_POOL = [
  "أبغى فيلا في الرياض تحت ٢ مليون",
  "وش رسوم الوسطاء العقاريين؟",
  "كيف أوثّق حسابي المهني؟",
  "ودّني أعرف عن خدمات الـ 3D",
  "عندكم شقق للإيجار في جدة؟",
  "كم الدفعة المقدمة للتمويل العقاري؟",
  "أبغى استراحة في الدمام",
  "وش أفضل حي للاستثمار بالرياض؟",
  "كيف أضيف عقاري للبيع عندكم؟",
  "فيه أراضي تجارية في القصيم؟",
  "وش الفرق بين الإيجار اليومي والسنوي؟",
  "أبغى دوبلكس في مكة",
  "كيف أتواصل مع مطوّر عقاري؟",
  "وش خطوات شراء عقار عندكم؟",
  "أبغى شقة فيها ٣ غرف وحمامين",
  "وش العقارات الجديدة هذا الأسبوع؟",
];

const SUGGESTIONS_EN_POOL = [
  "I want a villa in Riyadh under 2M SAR",
  "What are the broker fees?",
  "How do I verify my professional account?",
  "Tell me about 3D services",
  "Any apartments for rent in Jeddah?",
  "How much is the mortgage down payment?",
  "I want a rest house in Dammam",
  "Best neighborhood to invest in Riyadh?",
  "How do I list my property for sale?",
  "Any commercial land in Qassim?",
  "Daily vs yearly rent — what's the difference?",
  "I want a duplex in Makkah",
  "How do I contact a developer?",
  "What are the steps to buy a property?",
];

/* pick `n` random items */
const pickRandom = (arr: string[], n = 4) =>
  [...arr].sort(() => Math.random() - 0.5).slice(0, n);

/* very small markdown → JSX (bold, lists, line breaks) */
function renderMarkdown(text: string) {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const isBullet = /^\s*[-*]\s+/.test(line);
    const cleaned = line.replace(/^\s*[-*]\s+/, "");
    const withBold = cleaned.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => {
      if (seg.startsWith("**") && seg.endsWith("**")) {
        return (
          <strong key={j} className="font-bold text-slate-900">
            {seg.slice(2, -2)}
          </strong>
        );
      }
      return <span key={j}>{seg}</span>;
    });
    if (isBullet) {
      return (
        <div key={i} className="flex gap-2 my-1">
          <span className="text-main mt-1.5">•</span>
          <span className="flex-1">{withBold}</span>
        </div>
      );
    }
    if (line.trim() === "") return <div key={i} className="h-2" />;
    return (
      <p key={i} className="my-1">
        {withBold}
      </p>
    );
  });
}

export default function AIAssistant() {
  const locale = useLocale();
  const isRtl = locale === "ar";

  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  /* shuffle a fresh set of suggestions whenever the chat is opened */
  useEffect(() => {
    if (open) {
      setSuggestions(pickRandom(isRtl ? SUGGESTIONS_AR_POOL : SUGGESTIONS_EN_POOL));
    }
  }, [open, isRtl]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    {
      role: "assistant",
      content: isRtl
        ? "أهلاً! أنا مساعد SNA الذكي 🤖\nاسألني عن أي عقار، الرسوم، خدمات المنصة، أو أي حاجة تحب تعرفها."
        : "Hi! I'm SNA Smart Assistant 🤖\nAsk me about any property, fees, platform services, or anything you'd like to know.",
    },
  ]);
  const scrollRef = useRef<HTMLDivElement>(null);

  /* auto-scroll on new message */
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, loading]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const userMsg: Msg = { role: "user", content: trimmed };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const apiBase =
        process.env.NEXT_PUBLIC_API_URI ?? "http://localhost:8000";
      const res = await fetch(`${apiBase}/api/v1/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next,
          locale,
          siteUrl: window.location.origin,
        }),
      });
      const data = await res.json();

      if (!res.ok || data?.error) {
        const errText =
          typeof data?.error === "string"
            ? data.error
            : JSON.stringify(data?.error ?? {});
        console.error("[AIAssistant] API error:", errText);
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: isRtl
              ? `⚠️ خطأ من السيرفر:\n\n\`\`\`\n${errText}\n\`\`\``
              : `⚠️ Server error:\n\n\`\`\`\n${errText}\n\`\`\``,
          },
        ]);
        return;
      }

      const reply =
        data?.message ??
        (isRtl ? "ما وصلش رد من الموديل." : "No reply from the model.");
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      console.error("[AIAssistant] fetch failed:", err);
      const msg = err instanceof Error ? err.message : String(err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: isRtl
            ? `⚠️ تعذّر الاتصال بالخادم:\n${msg}`
            : `⚠️ Could not reach the server:\n${msg}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setOpen(true)}
            dir={isRtl ? "rtl" : "ltr"}
            className={`fixed bottom-6 z-[90] flex items-center gap-3 rounded-full bg-gradient-to-br from-main to-slate-900 px-5 py-4 text-white shadow-2xl shadow-main/30 hover:shadow-main/50 transition-shadow cursor-pointer group ${
              isRtl ? "left-6" : "right-6"
            }`}
            aria-label="Open AI Assistant"
          >
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full bg-main opacity-30 animate-ping" />
            <div className="relative flex items-center justify-center size-7">
              <Bot size={26} />
              <Sparkles
                size={12}
                className="absolute -top-1 -right-1 text-yellow-300"
              />
            </div>
            <span className="relative font-bold text-sm hidden md:inline">
              {isRtl ? "مساعد SNA الذكي" : "SNA AI Assistant"}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Chat Panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            dir={isRtl ? "rtl" : "ltr"}
            className={`fixed bottom-6 z-[90] w-[calc(100vw-3rem)] max-w-[400px] h-[600px] max-h-[calc(100vh-3rem)] flex flex-col rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden ${
              isRtl ? "left-6" : "right-6"
            }`}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-main p-5 text-white shrink-0">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 size-32 rounded-full bg-white blur-3xl" />
                <div className="absolute bottom-0 right-0 size-32 rounded-full bg-main blur-3xl" />
              </div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="size-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center ring-2 ring-white/20">
                    <Bot size={22} />
                  </div>
                  <div>
                    <h3 className="font-black text-base flex items-center gap-1.5">
                      {isRtl ? "مساعد SNA الذكي" : "SNA Smart Assistant"}
                      <Sparkles size={14} className="text-yellow-300" />
                    </h3>
                    <p className="text-xs text-white/70 flex items-center gap-1.5">
                      <span className="size-1.5 rounded-full bg-green-400 animate-pulse" />
                      {isRtl ? "متصل الآن" : "Online now"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="size-9 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-slate-50/60"
            >
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gradient-to-br from-main to-slate-900 text-white shadow-md shadow-main/20"
                        : "bg-white text-slate-700 border border-slate-100 shadow-sm"
                    }`}
                  >
                    {m.role === "assistant" ? (
                      <div className="space-y-0">{renderMarkdown(m.content)}</div>
                    ) : (
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    )}
                  </div>
                </motion.div>
              ))}

              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white border border-slate-100 rounded-2xl px-4 py-3 shadow-sm flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-main animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="size-2 rounded-full bg-main animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="size-2 rounded-full bg-main animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </motion.div>
              )}

              {/* Suggestions (only at start) */}
              {messages.length === 1 && !loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="pt-2 space-y-2"
                >
                  <p className="text-xs font-bold text-slate-400 px-1">
                    {isRtl ? "اقتراحات سريعة:" : "Quick suggestions:"}
                  </p>
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="block w-full text-start px-4 py-2.5 text-sm bg-white border border-slate-200 hover:border-main hover:bg-main/5 rounded-xl text-slate-600 hover:text-main transition-all cursor-pointer"
                    >
                      {s}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="shrink-0 border-t border-slate-100 bg-white p-3 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  isRtl ? "اكتب سؤالك..." : "Type your question..."
                }
                disabled={loading}
                className="flex-1 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3 text-sm font-medium text-slate-800 placeholder:text-slate-400 outline-none focus:border-main focus:bg-white transition-all disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="size-11 shrink-0 rounded-2xl bg-gradient-to-br from-main to-slate-900 text-white flex items-center justify-center hover:shadow-lg hover:shadow-main/30 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Send"
              >
                <Send size={16} className={isRtl ? "rotate-180" : ""} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
